import { AUTH, STATIC_ACCESS_TOKEN } from '@/config/settings'
import { Auth0Provider as _Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState, type ReactNode } from 'react'
import { clearAccessToken, getAccessToken, saveAccessToken } from './auth'

export function Auth0Provider({ children }: { children: ReactNode }) {
  if (STATIC_ACCESS_TOKEN) {
    return children
  }
  const { domain, clientId, authorizationParams } = AUTH

  if (!clientId) {
    console.error(
      'Need to define a clientId. Please set a valid client id in the VITE_CLIENT_ID variable in the .env file.',
    )
  }

  return (
    <_Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience: typeof authorizationParams?.audience === 'string' ? authorizationParams.audience : undefined,
        redirect_uri: window.location.origin,
        scope: typeof authorizationParams?.scope === 'string' ? authorizationParams.scope : undefined,
        organization: typeof authorizationParams?.organization === 'string' ? authorizationParams.organization : undefined,
      }}
      useRefreshTokens={true}
      cacheLocation='localstorage'
    >
      <Auth>{children}</Auth>
    </_Auth0Provider>
  )
}

function Auth({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    logout,
    user,
    error,
  } = useAuth0()
  const accessToken = getAccessToken()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const _logout = () =>
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      })

    if (!isAuthenticated) {
      if (!accessToken || isLoading) return

      clearAccessToken()
      void _logout()

      return
    }

    const fetchAccessToken = async () => {
      try {
        setLoading(true)
        const response = await getAccessTokenSilently()
        saveAccessToken(response)
        setLoading(false)
      } catch (error) {
        console.error('Error fetching access token:', error)
        clearAccessToken()
        void _logout()
      }
    }

    void fetchAccessToken()
  }, [
    accessToken,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    logout,
    user,
    error,
  ])

  if (loading) {
    return null
  }

  return children
}
